import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ProviderType,
  UniversalBookingAvailability,
  UniversalBookingBlockedDate,
  CreateAvailabilityInput,
} from "@/types/booking";

export function useUniversalAvailability(
  providerType: ProviderType | undefined,
  providerId: string | undefined
) {
  const [availability, setAvailability] = useState<UniversalBookingAvailability[]>([]);
  const [blockedDates, setBlockedDates] = useState<UniversalBookingBlockedDate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAvailability = useCallback(async () => {
    if (!providerType || !providerId) {
      setAvailability([]);
      setBlockedDates([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch availability slots
      const { data: availData, error: availError } = await supabase
        .from("universal_booking_availability")
        .select("*")
        .eq("provider_type", providerType)
        .eq("provider_id", providerId)
        .order("day_of_week", { ascending: true });

      if (availError) {
        console.error("Error fetching availability:", availError);
        toast.error("Error al cargar disponibilidad");
        return;
      }

      // Fetch blocked dates
      const { data: blockedData, error: blockedError } = await supabase
        .from("universal_booking_blocked_dates")
        .select("*")
        .eq("provider_type", providerType)
        .eq("provider_id", providerId)
        .gte("blocked_date", new Date().toISOString().split("T")[0])
        .order("blocked_date", { ascending: true });

      if (blockedError) {
        console.error("Error fetching blocked dates:", blockedError);
      }

      setAvailability((availData || []) as UniversalBookingAvailability[]);
      setBlockedDates((blockedData || []) as UniversalBookingBlockedDate[]);
    } catch (err) {
      console.error("Error in fetchAvailability:", err);
    } finally {
      setIsLoading(false);
    }
  }, [providerType, providerId]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const addAvailabilitySlot = async (
    dayOfWeek: number,
    startTime: string,
    endTime: string
  ): Promise<boolean> => {
    if (!providerType || !providerId) {
      toast.error("Información del proveedor no disponible");
      return false;
    }

    try {
      const { error } = await supabase
        .from("universal_booking_availability")
        .insert({
          provider_type: providerType,
          provider_id: providerId,
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
          is_active: true,
        });

      if (error) {
        console.error("Error adding availability slot:", error);
        toast.error("Error al añadir horario");
        return false;
      }

      toast.success("Horario añadido");
      await fetchAvailability();
      return true;
    } catch (err) {
      console.error("Error in addAvailabilitySlot:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const removeAvailabilitySlot = async (slotId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_availability")
        .delete()
        .eq("id", slotId);

      if (error) {
        console.error("Error removing availability slot:", error);
        toast.error("Error al eliminar horario");
        return false;
      }

      toast.success("Horario eliminado");
      await fetchAvailability();
      return true;
    } catch (err) {
      console.error("Error in removeAvailabilitySlot:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const toggleSlotActive = async (
    slotId: string,
    isActive: boolean
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_availability")
        .update({ is_active: isActive })
        .eq("id", slotId);

      if (error) {
        console.error("Error toggling slot:", error);
        toast.error("Error al actualizar horario");
        return false;
      }

      await fetchAvailability();
      return true;
    } catch (err) {
      console.error("Error in toggleSlotActive:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const blockDate = async (
    date: string,
    reason?: string
  ): Promise<boolean> => {
    if (!providerType || !providerId) {
      toast.error("Información del proveedor no disponible");
      return false;
    }

    try {
      const { error } = await supabase
        .from("universal_booking_blocked_dates")
        .insert({
          provider_type: providerType,
          provider_id: providerId,
          blocked_date: date,
          reason: reason || null,
        });

      if (error) {
        if (error.code === "23505") {
          toast.error("Esta fecha ya está bloqueada");
        } else {
          console.error("Error blocking date:", error);
          toast.error("Error al bloquear fecha");
        }
        return false;
      }

      toast.success("Fecha bloqueada");
      await fetchAvailability();
      return true;
    } catch (err) {
      console.error("Error in blockDate:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const unblockDate = async (blockedDateId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_blocked_dates")
        .delete()
        .eq("id", blockedDateId);

      if (error) {
        console.error("Error unblocking date:", error);
        toast.error("Error al desbloquear fecha");
        return false;
      }

      toast.success("Fecha desbloqueada");
      await fetchAvailability();
      return true;
    } catch (err) {
      console.error("Error in unblockDate:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  // Get availability for a specific day of week
  const getAvailabilityForDay = (dayOfWeek: number) => {
    return availability.filter(
      (slot) => slot.day_of_week === dayOfWeek && slot.is_active
    );
  };

  // Check if a date is blocked
  const isDateBlocked = (date: string) => {
    return blockedDates.some((bd) => bd.blocked_date === date);
  };

  return {
    availability,
    activeAvailability: availability.filter((a) => a.is_active),
    blockedDates,
    isLoading,
    addAvailabilitySlot,
    removeAvailabilitySlot,
    toggleSlotActive,
    blockDate,
    unblockDate,
    getAvailabilityForDay,
    isDateBlocked,
    refetch: fetchAvailability,
  };
}
